<script lang="ts" setup>
import {
    ProperCredentialCreateModal,
    ProperCredentialDetail,
    ProperCredentialsFilterBar,
    ProperCredentialsList,
    useAjiacoParams,
} from "@propertechnologies/ajiaco";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useMessage } from "naive-ui";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmationModal from "../../components/ConfirmationModal.vue";
import { getUserEmail } from "../../config/firebase";
import { useOrganization } from "../../hooks/useOrganization";
import type {
    CreateCredentialPayloadPortal,
    UpdateCredentialPayloadPortal,
} from "../../services/credentials/credentials.service";
import {
    createCredential,
    deleteCredential,
    getCredentials,
    getUtilityVendors,
    updateCredential,
} from "../../services/credentials/credentials.service";
import type { Credential, UtilityVendor } from "../../services/credentials/model";

const { organization } = useOrganization();
const router = useRouter();
const route = useRoute();
const message = useMessage();

// Must match Ajiaco ProperCredentialsFilterBar: "ALL" | "PMS" | "BANK" | "VENDOR" | "OTHER"
type CredentialTypeFilter = "ALL" | "PMS" | "BANK" | "VENDOR" | "OTHER";
type CredentialFormType = "PMS" | "BANK" | "VENDOR" | "OTHER";

const {
    search: searchQuery,
    types: selectedTypesParam,
    credential: credentialIdParam,
} = useAjiacoParams(
    {
        search: String,
        types: [] as string[],
        credential: String,
    },
    { router, route, writeMode: "replace" },
);

const selectedCredential = ref<Credential | null>(null);
const showCreateModal = ref(false);

// Detail ref is used only to ask for unsaved changes before switching selection
const credentialDetailRef = ref<InstanceType<typeof ProperCredentialDetail> | null>(null);
const showUnsavedChangesModal = ref(false);
const pendingCredentialSelection = ref<Credential | null>(null);

// Delete confirmation modal
const showDeleteModal = ref(false);
const pendingDeleteId = ref<string | null>(null);

const unsavedChangesContent = computed(() => {
    if (!selectedCredential.value) {
        return "You have unsaved changes. Are you sure you want to discard them?";
    }

    const name = selectedCredential.value.name || "this credential";
    return `You have unsaved changes for "${name}". Are you sure you want to discard them and select another credential?`;
});

const customerId = computed(() => organization.value?.id || "");
const customerName = computed(
    () => organization.value?.name || selectedCredential.value?.customerName || "",
);

const {
    data: credentials,
    isLoading,
    refetch,
} = useQuery({
    queryKey: computed(() => ["credentials", customerId.value]),
    queryFn: () => getCredentials(),
    enabled: computed(() => !!customerId.value),
});

// sync selectedCredential with URL param when credentials load or param changes
watch(
    [() => credentialIdParam.value, credentials],
    ([newId, creds]) => {
        if (!newId) {
            if (selectedCredential.value) {
                selectedCredential.value = null;
            }
            return;
        }
        if (!creds) return;

        const credential = creds.find((c) => c.id === newId);
        if (credential && selectedCredential.value?.id !== credential.id) {
            selectedCredential.value = credential;
        }
    },
    { immediate: true },
);

// update URL when selectedCredential changes
watch(
    () => selectedCredential.value?.id,
    (newId) => {
        if (credentialIdParam.value !== newId) {
            credentialIdParam.value = newId || undefined;
        }
    },
);

/**
 * Utility vendors – used to populate the vendor selector when creating
 * a new credential of type "VENDOR".
 */
const { data: utilityVendors } = useQuery({
    queryKey: computed(() => ["utility-vendors"]),
    queryFn: async () => getUtilityVendors(),
});

const vendorOptions = computed(() => {
    if (!utilityVendors.value) {
        return [];
    }
    const items = utilityVendors.value.items || [];
    return items
        .map((vendor: UtilityVendor) => ({
            label: vendor.name,
            value: vendor.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
});

const filteredCredentials = computed(() => {
    if (!credentials.value) return [];

    let filtered = [...credentials.value];

    const types = selectedTypesParam.value as CredentialTypeFilter[];
    if (types.length > 0 && !types.includes("ALL")) {
        filtered = filtered.filter((cred) => {
            return types.some((type) => {
                if (type === "OTHER") {
                    return cred.type === "OTHER" || !cred.type;
                }
                if (type === "VENDOR") {
                    return cred.type === "VENDOR" || cred.type === "UTILITY";
                }
                return cred.type === type;
            });
        });
    }

    if (searchQuery.value?.trim()) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter((cred) => {
            return (
                cred.name?.toLowerCase().includes(query) ||
                cred.userName?.toLowerCase().includes(query) ||
                cred.url?.toLowerCase().includes(query) ||
                cred.vendor?.name?.toLowerCase().includes(query)
            );
        });
    }

    // sort by createdAt descending (newest first)
    filtered.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
    });

    return filtered;
});

const listItems = computed(() => {
    return filteredCredentials.value.map((cred) => ({
        id: cred.id,
        name: cred.name,
        userName: cred.userName,
        url: cred.url,
        type: cred.type ?? undefined,
        lastConnectionStatus:
            cred.lastConnectionStatus === "success"
                ? true
                : cred.lastConnectionStatus != null
                  ? false
                  : undefined,
    }));
});

const detailCredential = computed(() => {
    const cred = selectedCredential.value;
    if (!cred) return null;

    return {
        id: cred.id,
        name: cred.name,
        userName: cred.userName,
        password: cred.password,
        url: cred.url,
        type: (cred.type || undefined) as CredentialFormType | undefined,
        systemName: cred.systemName || undefined,
        bankInstitutionName: cred.bankInstitutionName || undefined,
        routingNumber: cred.routingNumber || undefined,
        vendorName: cred.vendor?.name || undefined,
        vendorId: cred.vendorId || undefined,
        twoFaPhone: cred.twoFaPhone || undefined,
        twoFaMethod: cred.twoFaMethod || undefined,
        notes: cred.notes,
        automationEnabled: !!cred.automationEnabled,
        lastConnectionStatus:
            cred.lastConnectionStatus === "success"
                ? true
                : cred.lastConnectionStatus != null
                  ? false
                  : undefined,
        createdAt: cred.createdAt,
        createdBy: cred.createdBy || undefined,
        updatedAt: cred.updatedAt,
        updatedBy: cred.updatedBy || undefined,
        customerName: cred.customerName,
    };
});

/**
 * Data mutations
 */
const { mutate: mutateCreate, isPending: isCreating } = useMutation({
    mutationFn: async (payload: CreateCredentialPayloadPortal) => createCredential(payload),
    onSuccess: (createdCredential) => {
        // after creating, refetch so we get decrypted password
        handleRefetchAndReselect(createdCredential.id);
        showCreateModal.value = false;

        message.success("Credential created");
    },
});

const { mutate: mutateUpdate, isPending: isUpdating } = useMutation({
    mutationFn: async (payload: UpdateCredentialPayloadPortal) => updateCredential(payload),
    onSuccess: (updatedCredential) => {
        handleRefetchAndReselect(updatedCredential.id);
        message.success("Credential updated");
    },
});

const { mutate: mutateDelete, isPending: isDeleting } = useMutation({
    mutationFn: async (credentialId: string) =>
        deleteCredential(credentialId, getUserEmail() || null),
    onSuccess: () => {
        selectedCredential.value = null;
        refetch();

        message.success("Credential deleted");
    },
});

const isSaving = computed(() => isCreating.value || isUpdating.value);

/**
 * Helpers
 */
function handleRefetchAndReselect(credentialId?: string) {
    const currentSelectedId = credentialId || selectedCredential.value?.id;

    refetch().then(() => {
        if (currentSelectedId && credentials.value) {
            const updatedCredential = credentials.value.find((c) => c.id === currentSelectedId);
            selectedCredential.value = updatedCredential ?? null;
        }
    });
}

function handleSelectCredentialFromList(item: { id: string | number }) {
    const next = filteredCredentials.value.find((c) => c.id === String(item.id)) ?? null;

    if (
        selectedCredential.value &&
        credentialDetailRef.value?.hasUnsavedChanges &&
        credentialDetailRef.value.hasUnsavedChanges()
    ) {
        pendingCredentialSelection.value = next;
        showUnsavedChangesModal.value = true;
        return;
    }

    selectedCredential.value = next;
}

function confirmDiscardChanges() {
    showUnsavedChangesModal.value = false;
    if (pendingCredentialSelection.value) {
        selectedCredential.value = pendingCredentialSelection.value;
        pendingCredentialSelection.value = null;
    }
}

function cancelDiscardChanges() {
    showUnsavedChangesModal.value = false;
    pendingCredentialSelection.value = null;
}

function handleBackToList() {
    if (
        selectedCredential.value &&
        credentialDetailRef.value?.hasUnsavedChanges &&
        credentialDetailRef.value.hasUnsavedChanges()
    ) {
        pendingCredentialSelection.value = null;
        showUnsavedChangesModal.value = true;
        return;
    }

    selectedCredential.value = null;
}

function handleOpenCreateModal() {
    showCreateModal.value = true;
}

/**
 * handle save coming from the detail panel.
 * maps the ajiaco payload into our `UpdateCredentialPayload` and calls the service.
 */
function handleDetailSave(payload: {
    mode: "create" | "update";
    type: CredentialFormType | null;
    automationEnabled: boolean;
    form: {
        name: string;
        userName: string;
        password: string;
        url: string;
        twoFaPhone?: string;
        twoFaMethod?: string;
        notes?: string;
        systemName?: string;
        vendorId?: string;
        bankInstitutionName?: string;
        routingNumber?: string;
    };
}) {
    const credType = payload.type ?? "OTHER";
    const basePayload = {
        name: payload.form.name,
        user_name: payload.form.userName,
        password: payload.form.password || undefined,
        url: payload.form.url,
        notes: payload.form.notes || "",
        vendor_id: credType === "VENDOR" ? payload.form.vendorId || null : null,
        two_fa_phone: payload.form.twoFaPhone || null,
        two_fa_method: payload.form.twoFaMethod || null,
        automation_enabled: payload.automationEnabled || false,
        type: payload.type || null,
        system_name: credType === "PMS" ? payload.form.systemName || null : null,
        bank_institution_name:
            credType === "BANK" ? payload.form.bankInstitutionName || null : null,
        routing_number: credType === "BANK" ? payload.form.routingNumber || null : null,
    };

    if (payload.mode === "create") {
        mutateCreate({
            ...basePayload,
            created_by: getUserEmail() || null,
        });
        return;
    }

    if (!selectedCredential.value) return;

    mutateUpdate({
        id: selectedCredential.value.id,
        ...basePayload,
        updated_by: getUserEmail() || null,
    });
}

/**
 * handle create event coming from the modal.
 * maps the ajiaco payload into our `CreateCredentialPayload` and calls the service.
 */
function handleCreateFromModal(payload: {
    type: CredentialFormType | null;
    automationEnabled: boolean;
    form: {
        name: string;
        userName: string;
        password: string;
        url: string;
        twoFaPhone?: string;
        twoFaMethod?: string;
        notes?: string;
        systemName?: string;
        vendorId?: string;
        bankInstitutionName?: string;
        routingNumber?: string;
    };
}) {
    console.log("[Portal Credentials] handleCreateFromModal called", payload);

    mutateCreate({
        name: payload.form.name,
        user_name: payload.form.userName,
        password: payload.form.password || undefined,
        url: payload.form.url,
        notes: payload.form.notes || "",
        vendor_id: (payload.type ?? "OTHER") === "VENDOR" ? payload.form.vendorId || null : null,
        two_fa_phone: payload.form.twoFaPhone || null,
        two_fa_method: payload.form.twoFaMethod || null,
        automation_enabled: payload.automationEnabled || false,
        type: payload.type || null,
        system_name: (payload.type ?? "OTHER") === "PMS" ? payload.form.systemName || null : null,
        bank_institution_name:
            (payload.type ?? "OTHER") === "BANK" ? payload.form.bankInstitutionName || null : null,
        routing_number:
            (payload.type ?? "OTHER") === "BANK" ? payload.form.routingNumber || null : null,
        created_by: getUserEmail() || null,
    });
}

function handleDelete(id: string | number | undefined) {
    if (id == null) return;
    pendingDeleteId.value = String(id);
    showDeleteModal.value = true;
}

function confirmDelete() {
    if (pendingDeleteId.value) {
        mutateDelete(pendingDeleteId.value);
        pendingDeleteId.value = null;
    }
    showDeleteModal.value = false;
}

function cancelDelete() {
    pendingDeleteId.value = null;
    showDeleteModal.value = false;
}

const deleteConfirmationContent = computed(() => {
    if (!pendingDeleteId.value) return "";
    const cred = credentials.value?.find((c) => c.id === pendingDeleteId.value);
    const name = cred?.name || "this credential";
    return `Are you sure you want to delete "${name}"? This action cannot be undone.`;
});

function handleSeeAvailableBills(_id: string | number | undefined) {
    console.log("NOT IMPLEMENTED HERE", _id);
}
</script>

<template>
    <section class="p-3 sm:p-4 md:p-6 rounded-lg">
        <!-- Header: Search, filters, and actions -->
        <div>
            <div
                class="flex flex-col sm:flex-row gap-3 sm:gap-2 sm:justify-between sm:items-center mb-4"
            >
                <!-- Client badge + search and filters (left) -->
                <div class="flex flex-col gap-1 w-full sm:w-auto">
                    <div
                        v-if="customerName"
                        class="px-2 py-1 mb-3 -mt-5 bg-gray-100 text-[8px] sm:text-[10px] font-mono tracking-[0.14em] uppercase text-gray-600 w-fit"
                    >
                        {{ customerName }}
                    </div>
                    <ProperCredentialsFilterBar
                        v-model:search="searchQuery"
                        v-model:selected-types="selectedTypesParam"
                    />
                </div>

                <!-- Actions (right) -->
                <div class="flex gap-2 items-center w-full sm:w-auto justify-end">
                    <button
                        class="w-9 h-9 rounded-full bg-[#2D3250] text-white flex items-center justify-center hover:bg-[#1f2338] transition-colors flex-shrink-0"
                        @click="handleOpenCreateModal"
                    >
                        <span class="material-symbols-outlined text-lg">add</span>
                    </button>
                </div>
            </div>

            <!-- Responsive layout: single column on mobile, two columns on desktop -->
            <div
                class="grid grid-cols-1 md:grid-cols-2 border border-gray-300 rounded-lg overflow-hidden"
            >
                <!-- List: hidden on mobile when detail is shown, always visible on desktop -->
                <div
                    :class="{
                        'hidden md:block': selectedCredential,
                        block: !selectedCredential,
                    }"
                >
                    <proper-credentials-list
                        :credentials="listItems"
                        :loading="isLoading"
                        :selected-id="selectedCredential?.id || null"
                        :hide-last-border="!selectedCredential"
                        @select="handleSelectCredentialFromList"
                    />
                </div>

                <!-- Detail: only shown when selected -->
                <div v-if="selectedCredential" class="md:border-l md:border-gray-300">
                    <!-- Mobile back button -->
                    <div class="md:hidden px-4 py-3 border-b border-gray-300 bg-white">
                        <button
                            class="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                            @click="handleBackToList"
                        >
                            <span class="material-symbols-outlined text-lg">arrow_back</span>
                            <span>Back to list</span>
                        </button>
                    </div>
                    <proper-credential-detail
                        ref="credentialDetailRef"
                        :credential="detailCredential"
                        :saving="isSaving"
                        :deleting="isDeleting"
                        :show-automation="false"
                        :show-shared-services="false"
                        @save="handleDetailSave"
                        @cancel="handleBackToList"
                        @delete="handleDelete"
                        @see-available-bills="handleSeeAvailableBills"
                    />
                </div>

                <!-- Desktop placeholder when no selection -->
                <div
                    v-if="!selectedCredential"
                    class="hidden md:flex items-center justify-center bg-white border-l border-gray-300 min-h-[400px]"
                >
                    <p class="text-gray-400 text-center">Select a credential to view details</p>
                </div>
            </div>

            <!-- Create Credential Modal -->
            <proper-credential-create-modal
                v-model:show="showCreateModal"
                :customer-name="customerName"
                :saving="isCreating"
                :vendor-options="vendorOptions"
                :show-automation="false"
                @create="handleCreateFromModal"
            />
        </div>
    </section>

    <confirmation-modal
        v-if="showUnsavedChangesModal"
        title="Unsaved Changes"
        :content="unsavedChangesContent"
        negative-text="Cancel"
        positive-text="Discard Changes"
        @positive-click="confirmDiscardChanges"
        @negative-click="cancelDiscardChanges"
    />

    <confirmation-modal
        v-if="showDeleteModal"
        danger
        title="Confirm Deletion"
        :content="deleteConfirmationContent"
        negative-text="Cancel"
        positive-text="Delete"
        @positive-click="confirmDelete"
        @negative-click="cancelDelete"
    />
</template>
